import React from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import { DEVELOPER_THEME } from '../../types/common';

interface NameNicknameDisplayProps {
  name: string;
  nickname?: string;
  // テストアカウント表示
  isTestAccount?: boolean;
  // レイアウト関連
  direction?: 'column' | 'row';
  spacing?: number;
  // 名前のスタイル
  nameFontSize?: string;
  nameFontWeight?: string | number;
  nameColor?: string;
  nameVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2';
  // ニックネームのスタイル
  showNicknameLabel?: boolean;
  nicknameLabel?: string;
  nicknameFontSize?: string;
  nicknameFontWeight?: string | number;
  nicknameColor?: string;
  nicknameValueFontWeight?: string | number;
  nicknameValueColor?: string;
  // ホバー効果
  enableHoverEffect?: boolean;
  nameHoverClass?: string;
  nicknameHoverClass?: string;
  // クラス名
  className?: string;
  // 追加のスタイル
  sx?: SxProps<Theme>;
  // ニックネームラッパーのスタイル
  nicknameSx?: SxProps<Theme>;
}

/**
 * 名前とニックネームを表示するコンポーネント
 */
const NameNicknameDisplay: React.FC<NameNicknameDisplayProps> = ({
  name,
  nickname,
  // テストアカウント表示
  isTestAccount = false,
  // レイアウト関連
  direction = 'column',
  spacing = 0.5,
  // 名前のスタイル
  nameFontSize = '1rem',
  nameFontWeight = 'bold',
  nameColor,
  nameVariant = 'body1',
  // ニックネームのスタイル
  showNicknameLabel = true,
  nicknameLabel = 'ニックネーム:',
  nicknameFontSize = '0.85rem',
  nicknameFontWeight,
  nicknameColor = 'text.secondary',
  nicknameValueFontWeight = 'bold',
  nicknameValueColor,
  // ホバー効果
  enableHoverEffect = false,
  nameHoverClass,
  nicknameHoverClass,
  // クラス名
  className,
  // 追加のスタイル
  sx,
  // ニックネームラッパーのスタイル
  nicknameSx
}) => {
  // スタイル設定
  const containerStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: direction,
    ...(direction === 'column' ? { gap: spacing } : { alignItems: 'center', gap: 1 }),
    ...sx
  };

  const nameStyles: SxProps<Theme> = {
    fontWeight: nameFontWeight,
    fontSize: nameFontSize,
    ...(nameColor ? { color: nameColor } : {})
  };

  const nicknameStyles: SxProps<Theme> = {
    fontSize: nicknameFontSize,
    ...(nicknameColor ? { color: nicknameColor } : {}),
    ...(nicknameFontWeight ? { fontWeight: nicknameFontWeight } : {}),
    ...(nicknameSx || {})
  };

  const nicknameValueStyles: SxProps<Theme> = {
    fontWeight: nicknameValueFontWeight,
    ...(nicknameValueColor ? { color: nicknameValueColor } : {})
  };

  return (
    <Box sx={containerStyles} className={className}>
      <Typography
        variant={nameVariant}
        sx={nameStyles}
        className={enableHoverEffect ? 'name-hover' : nameHoverClass}
      >
        {isTestAccount && (
          <Box
            component="span"
            sx={{
              color: DEVELOPER_THEME.DARK,
              fontWeight: 'bold',
              marginRight: 0.5,
              border: `1px solid ${DEVELOPER_THEME.DARK}`,
              padding: '1px 4px',
              fontSize: '0.7em',
              borderRadius: 0
            }}
          >
            TEST
          </Box>
        )}
        {name}
      </Typography>

      {nickname && (
        <Typography
          variant="body2"
          sx={nicknameStyles}
        >
          {showNicknameLabel && nicknameLabel}{' '}
          <Box
            component="span"
            sx={nicknameValueStyles}
            className={enableHoverEffect ? 'nickname-hover' : nicknameHoverClass}
          >
            {nickname}
          </Box>
        </Typography>
      )}
    </Box>
  );
};

export default NameNicknameDisplay;